import { ThemeEvents, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

class ProductInventory extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  disconnectedCallback() {
    const closestSection = this.closest(".shopify-section, dialog");
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    // The picker announces the new variant the moment it is clicked, with no
    // HTML — that is only fetched afterwards, on idle. Waiting for it left the
    // badge showing the previous shade's wording for as long as the round trip
    // took, so render from the map inventory-data.liquid embedded alongside us
    // and let the morph below confirm it when the HTML does arrive.
    if (!event.detail.data.html) {
      this.#renderFromData(event.detail.resource?.id);
      return;
    }

    // Find the correct product-inventory in HTML by matching productId
    // This prevents taking inventory from wrong product (e.g. recommendations)
    const productId = this.dataset.productId;
    let newInventory = null;

    if (productId) {
      // Find all product-inventory elements and match by productId
      const allInventories = event.detail.data.html.querySelectorAll("product-inventory");
      for (const inventory of allInventories) {
        if (inventory.dataset.productId === productId) {
          newInventory = inventory;
          break;
        }
      }
    }

    // Fallback: if no productId match, use first inventory (for single product pages)
    if (!newInventory) {
      newInventory = event.detail.data.html.querySelector("product-inventory");
    }

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
  };

  /**
   * Repaints the badge from the per-variant data on this element, without a fetch.
   * Only the status classes and the sentence are touched; the data attribute is
   * left alone, because the next click reads it again.
   * @param {string|number|undefined} variantId - The newly selected variant.
   */
  #renderFromData(variantId) {
    if (!variantId) return;

    const raw = this.dataset.inventory;
    if (!raw) return;

    let entry;
    try {
      entry = JSON.parse(raw)[String(variantId)];
    } catch {
      return;
    }
    // A variant absent from the map means a combined-listing switch to another
    // product, whose map this element does not carry. Leave the badge alone;
    // the fetched HTML will carry the truth.
    if (!entry) return;

    const text = this.querySelector(".product-inventory__text");
    if (!text) return;
    text.textContent = entry.t;

    for (const el of this.querySelectorAll(".product-inventory__status, .product-inventory__dot")) {
      el.className = el.className.replace(
        /(product-inventory__(?:status|dot))--\S+/g,
        `$1--${entry.s}`
      );
    }
  }
}

if (!customElements.get("product-inventory")) {
  customElements.define("product-inventory", ProductInventory);
}
